import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Organisation } from "../types";
import {
  getMyOrganisations,
  saveOrganisation,
} from "../services/storageService";
import { generateId } from "../services/utils";

interface WorkspaceContextType {
  organisations: Organisation[];
  currentOrg: Organisation | null;
  isLoading: boolean;
  error: string | null;
  switchOrg: (slug: string) => void;
  refreshOrgs: () => Promise<Organisation[]>;
  setCurrentOrg: (org: Organisation | null) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
};

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { getToken } = useAuth();
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organisation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshOrgs = useCallback(async () => {
    if (!user) return [];
    try {
      const token = await getToken();
      const myOrgs = await getMyOrganisations(token || undefined);
      setOrganisations(myOrgs);
      return myOrgs;
    } catch (e) {
      console.error("Failed to fetch organisations:", e);
      setError("Failed to load workspaces.");
      return [];
    }
  }, [user, getToken]);

  useEffect(() => {
    if (!isUserLoaded) return;
    if (!user) {
      setOrganisations([]);
      setCurrentOrg(null);
      setIsLoading(false);
      return;
    }

    const init = async () => {
      setIsLoading(true);
      try {
        const token = await getToken();
        let myOrgs = await getMyOrganisations(token || undefined);

        if (myOrgs.length === 0) {
          const firstName = (user.firstName || "user")
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
          const lastName = (user.lastName || "")
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
          const random = Math.floor(Math.random() * 900 + 100);
          const personalSlug = `${firstName}-${lastName}-${random}`;

          const defaultOrg: Organisation = {
            id: generateId(),
            name: "My Workspace",
            slug: personalSlug,
            type: "PERSONAL",
            ownerId: user.id,
            memberIds: [],
            role: "MAINTAINER",
          };
          const savedOrg = await saveOrganisation(
            defaultOrg,
            token || undefined,
          );
          myOrgs = [savedOrg];
        }

        setOrganisations(myOrgs);
      } catch (e) {
        console.error("Failed to initialize organisations:", e);
        setError("Failed to load workspace. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, isUserLoaded, getToken]);

  const switchOrg = useCallback(
    (slug: string) => {
      const found = organisations.find((o) => o.slug === slug);
      if (found) {
        setCurrentOrg(found);
        localStorage.setItem("lastVisitedOrgSlug", slug);
      }
    },
    [organisations],
  );

  return (
    <WorkspaceContext.Provider
      value={{
        organisations,
        currentOrg,
        isLoading,
        error,
        switchOrg,
        refreshOrgs,
        setCurrentOrg,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
